import { Button, DatePicker, Flex, Form, Input, Layout, Space, Table, TableProps, Tooltip } from "antd";
import { Content } from "antd/es/layout/layout";
import { KeyboardEventHandler, useEffect } from "react";
import { getLogEntriesTaskExec, TaskExec, TaskExecLogEntry, TaskExecLogEntryFilter } from "../../apis/mgmt/api";
import { DataForm } from "../common/DataForm";
import { DataFormWithButtonsLayout } from "../common/DataFormWithButtonsLayout";
import { DataViewProps, useStatefulDataView } from "../common/DataView";
import { useEditableWithState } from "../common/Editable";
import { ListProps, useStatefulList } from "../common/List";
import { Modal } from "../common/Modal";
import { ToggleableProps, useStatefulToggleable } from "../common/Toggleable";
import dayjs from "dayjs";
import { FilterOutlined, LeftOutlined, RightOutlined, VerticalLeftOutlined, VerticalRightOutlined } from "@ant-design/icons";



/**
 * Transform instant to locale string with milliseconds
 */
const toLocaleString = (instant: string) => {
    const date = new Date(instant);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const milliseconds = String(date.getMilliseconds()).padStart(3, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
}


function TaskExecLogEntryList(props: ListProps<TaskExecLogEntry>) {
    return (
        <Layout className="h-100">
            <Content style={{ overflow: 'auto' }} className="mig-log">
                {props.data && props.data.map((log: TaskExecLogEntry, index: number) => (
                    <div key={index}>
                        <span className="timestamp">{log.instant && toLocaleString(log.instant)}</span>
                        <span className="level">{log.level && log.level.padEnd(5)}</span>
                        <span className="op">{log.op}</span>
                        <span className="separator">:</span>
                        <span className="exception">{log.exceptionType}</span>
                        <span className="message">{log.message}</span>
                    </div>
                ))}
            </Content>
        </Layout>
    );
}



function TaskExecLogEntryFilterPopup(props: 
    DataViewProps<TaskExecLogEntryFilter> &
    ToggleableProps
) {

    const editableProps = useEditableWithState(props, props);
    const emptyToNull = (v: string | undefined) => v ? v : null;

    return (
        <Modal title="Filtro" toggle={props} onCancel={props.hide}>
        <DataFormWithButtonsLayout editableProps={editableProps}>
            <DataForm {...editableProps} >
                <Form.Item name="from"
                    getValueProps={(value) => ({ value: value ? dayjs(value) : undefined })}
                    getValueFromEvent={(value) => (value ? value.toISOString() : undefined)}
                >
                    <DatePicker showTime style={{ width: "100%" }} placeholder="from" />
                </Form.Item>

                <Form.Item name="to"
                    getValueProps={(value) => ({ value: value ? dayjs(value) : undefined })}
                    getValueFromEvent={(value) => (value ? value.toISOString() : undefined)}
                >
                    <DatePicker showTime style={{ width: "100%" }} placeholder="to" />
                </Form.Item>

                <Form.Item name="thread" normalize={emptyToNull}>
                    <Input placeholder="thread" />
                </Form.Item>

                <Form.Item name="level" normalize={emptyToNull}>
                    <Input placeholder="level" />
                </Form.Item>

                <Form.Item name="loggerName" normalize={emptyToNull}>
                    <Input placeholder="logger" />
                </Form.Item>

                <Form.Item name="message" normalize={emptyToNull}>
                    <Input placeholder="message" />
                </Form.Item>

                <Form.Item name="domain" normalize={emptyToNull}>
                    <Input placeholder="domain" />
                </Form.Item>

                <Form.Item name="platform" normalize={emptyToNull}>
                    <Input placeholder="platform" />
                </Form.Item>

                <Form.Item name="platformDomain" normalize={emptyToNull}>
                    <Input placeholder="platformDomain" />
                </Form.Item>

                <Form.Item name="taskExecId" normalize={emptyToNull}>
                    <Input placeholder="taskExecId" />
                </Form.Item>
            </DataForm>
        </DataFormWithButtonsLayout>
    </Modal>
    );
}

export const TaskExecLog = function (props:{
    taskExec: TaskExec | undefined,
    toggle: ToggleableProps
}) {

    const { taskExec } = props;
    const listProps = useStatefulList<TaskExecLogEntry>();
    const filteredListProps = useStatefulList<TaskExecLogEntry>();
    const filter = useStatefulDataView<TaskExecLogEntryFilter>({ message: '' });
    const filterToggle = useStatefulToggleable();
    
    useEffect(() => {
        if (taskExec && taskExec.id) {
            getLogEntriesTaskExec({ 
                ...filter.data, taskExecId: taskExec.id 
            }).then(data => {
                listProps.setData(data.data);
                filteredListProps.setData(data.data);
        })
        } else {
            listProps.setData([]);
            filteredListProps.setData([]);
        }
    }, [taskExec, filter.data]);

    const onKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
        console.log("KEY DOWN")
        if ((e.ctrlKey || e.metaKey) && e.key === "f") {
            e.preventDefault(); // Evita el comportamiento predeterminado del navegador
            filterToggle.show();
        }
    }

    return taskExec && taskExec.id && 
        <Modal mode="FullPage" onKeyDown={onKeyDown}
            title={
                <Flex justify="space-between" align="flex-start">
                    <div>
                        <span>Execution log of {taskExec.name}</span><small> ({taskExec.id}) -
                        {taskExec?.started && ` From ${new Date(taskExec?.started).toLocaleString()}`}
                        {taskExec?.finished && ` to ${new Date(taskExec?.finished).toLocaleTimeString()}` || '...'}
                        </small>
                    </div>
                    <Space.Compact style={{ marginRight: '25px' }}>
                        <Tooltip title="Like">
                            <Button icon={<VerticalRightOutlined />} />
                        </Tooltip>
                        <Tooltip title="Comment">
                            <Button icon={<LeftOutlined />} />
                        </Tooltip>
                        <Tooltip title="Star">
                            <Button icon={<FilterOutlined />} />
                        </Tooltip>
                        <Tooltip title="Heart">
                            <Button icon={<RightOutlined />} />
                        </Tooltip>
                        <Tooltip title="Share">
                            <Button icon={<VerticalLeftOutlined />} />
                        </Tooltip>
                    </Space.Compact>
                </Flex>
            }
            toggle={props.toggle}
        >
            <TaskExecLogEntryList {...filteredListProps} />
            <TaskExecLogEntryFilterPopup {...filter} {...filterToggle} />
        </Modal>
        || null;

};
